// Analytics service for Google Analytics 4 and backend analytics
// Uses environment variable for measurement ID to support different environments

import api from './api';
import authService from './auth';
import { maskTokenPath } from '../utils/tokenPathMask';
import { generatePageViewId } from '../utils/engagementBeacon';

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-4XZCZ71W24';

// Cookie mode is the primary auth surface; header mode is the fallback. The
// batch transport below has to know which one is live, because sendBeacon can
// carry cookies but CANNOT carry an Authorization header.
const USE_COOKIES = process.env.REACT_APP_USE_COOKIES === 'true';

// Generate or retrieve session ID for this session
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Same as above but never throws: the batch queue runs from event handlers and
// page-unload listeners, where a Safari-private-mode storage throw must not
// take the caller down with it. A session-less event is still a useful event.
const safeSessionId = () => {
  try {
    return getSessionId();
  } catch (error) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// ANONYMOUS-LANE ID (owner option A, 2026-10-19) - keep this block together.
//
// The consent-INDEPENDENT anonymous lanes - the /analytics/page-view beacon
// (`page_view`) and the singular-lane names the backend treats as anonymous
// (`utm_visit`, `client_logo_click`; SINGULAR_ONLY_EVENT_NAMES in
// backend/app/schemas/analytics.py) - must not carry `analytics_session_id`:
// that id also rides the identified lanes (the product-event batch, the
// lead_* trio), so sharing it would let pre-signup clickstream be joined to an
// account. They carry this id instead, in the same `session_id` field (the
// backend is unchanged). It is random, created on first use, held ONLY in this
// module (one per tab and page load, so an SPA navigation keeps it), never
// written to any storage and never sent on an identified lane. Nothing on the
// anonymous path calls getSessionId(), so a visitor who never reaches an
// identified lane never gets an analytics_session_id at all.
// ---------------------------------------------------------------------------
let anonymousLaneId = null;

/** This page load's in-memory anonymous-lane id. Never stored, never throws. */
const getAnonymousLaneId = () => {
  if (anonymousLaneId === null) anonymousLaneId = generatePageViewId();
  return anonymousLaneId;
};

/** The singular-lane names that take the anonymous-lane id, not the session's. */
const ANONYMOUS_SINGULAR_EVENTS = new Set(['utm_visit', 'client_logo_click']);
// ------------------------------------------------- end of ANONYMOUS-LANE ID

// ---------------------------------------------------------------------------
// Route normalization
// ---------------------------------------------------------------------------

/**
 * True for a path segment that identifies ONE record rather than a place in the
 * app: a uuid, a numeric id, or a long opaque token.
 *
 * The last clause is the defensive one. A route pattern describes a SHAPE, so
 * any segment that is plainly a generated identifier is folded even when no
 * route declares it as a param today - the cost of a wrong fold is one blurred
 * row, the cost of a miss is a per-record value in an aggregate table. It is
 * NOT what protects the supporter tokens: those live in the QUERY STRING, which
 * this function never sees because normalizeRoutePath drops it outright.
 *
 * Anything >= 16 chars of url-safe token charset carrying at least one digit
 * qualifies; real route words ("dashboard", "profession-descriptions") are
 * either shorter or digit-free.
 *
 * @param {string} segment
 * @param {boolean} allowOpaque false to skip the long-token clause (see
 *   OPAQUE_EXEMPT_PREFIXES)
 */
const isIdSegment = (segment, allowOpaque = true) => {
  if (!segment) return false;
  if (/^[0-9]+$/.test(segment)) return true;
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
  ) {
    return true;
  }
  if (!allowOpaque) return false;
  return segment.length >= 16 && /[0-9]/.test(segment) && /^[A-Za-z0-9_-]+$/.test(segment);
};

/**
 * Routes whose segments are HUMAN slugs, exempt from the opaque-token fold.
 *
 * `/blog/top-10-ways-to-find-leads` is 25 url-safe chars with a digit in it -
 * indistinguishable from a token by shape, and folding it to `/blog/:id`
 * collapses every English article into one row, which is the exact opposite of
 * what the blog needs measured. uuid/numeric folding still applies here, so a
 * future numeric-id article route would still be folded.
 *
 * FUTURE-SLUG RISK, stated so it is a decision and not an oversight: this is a
 * prefix allowlist, so if a route under /blog/ ever carries a SECRET in a path
 * segment (a preview token, a share link), that secret would reach the
 * analytics table in the clear. Anything of that kind must either not live
 * under /blog/ or be removed from this list in the same change.
 */
const OPAQUE_EXEMPT_PREFIXES = ['blog'];

/**
 * `{page_location}` overriding gtag's own `dl` when the current address is a
 * token-bearing path (`/enterprise/pay/<token>`), else `{}` (#349 R3-03).
 *
 * @returns {{page_location?: string}}
 */
export const tokenSafePageLocation = () => {
  try {
    const { origin, pathname } = window.location;
    const masked = maskTokenPath(pathname);
    return masked !== pathname ? { page_location: `${origin}${masked}` } : {};
  } catch (error) {
    return {};
  }
};

/**
 * Turn a concrete path into the ROUTE PATTERN it instantiates.
 *
 *   /admin/users/8f1c...-b2  ->  /admin/users/:id
 *   /leads/42?tab=hot        ->  /leads/:id
 *   /                        ->  /
 *
 * The query string and hash are stripped unconditionally and never restored:
 * two live routes carry a token there (/supporter/confirm, /supporter/unsubscribe)
 * and a confirm token in an analytics row is manufacturable consent - the same
 * reasoning trackPageView() documents for GA4's page_location. That strip is
 * what protects those tokens; the opaque-segment fold below is a separate,
 * defensive rule about PATH segments.
 *
 * @param {string} path
 * @returns {string} normalized route pattern, capped at the column's 255 chars
 */
export const normalizeRoutePath = (path) => {
  if (typeof path !== 'string' || path.length === 0) return '/';

  // #349: a path-borne secret (`/enterprise/pay/<token>`) is folded by NAME
  // first, through the one shared rule - the id-shape fold below only catches
  // a token that happens to look like an id (e.g. carries a digit).
  let clean = maskTokenPath(path.split('?')[0].split('#')[0]);
  if (!clean.startsWith('/')) clean = `/${clean}`;
  // `/leads//42` and `/leads/42` are the same place. Collapsed BEFORE the split
  // so an empty segment cannot survive as a stray `//` in the pattern.
  clean = clean.replace(/\/{2,}/g, '/');

  const segments = clean.split('/');
  // segments[0] is always '' (leading slash), so the route's first real segment
  // is index 1 - that is what the exemption keys on.
  // Lowercased: a link to /Blog/... is the same route, and a case-sensitive
  // match would silently fold that article's slug to :id.
  const allowOpaque = !OPAQUE_EXEMPT_PREFIXES.includes(
    String(segments[1] || '').toLowerCase(),
  );

  const normalized = segments
    .map((segment) => (isIdSegment(segment, allowOpaque) ? ':id' : segment))
    .join('/');

  const trimmed =
    normalized.length > 1 && normalized.endsWith('/')
      ? normalized.slice(0, -1)
      : normalized;

  return trimmed.slice(0, 255) || '/';
};

// ---------------------------------------------------------------------------
// Product-analytics batch queue
// ---------------------------------------------------------------------------
//
// Product events are high-frequency and individually worthless - what matters
// is the shape of the funnel across a session. One POST per event would be a
// request per card click; instead they accumulate in a module-level queue and
// leave in batches, on a ~10s timer and whenever the page is going away.
//
// CONSENT. Product events are gated on the SAME `analytics` cookie-consent
// category as the GA4 surface (owner decision) - `trackEvent` only enqueues
// them once consent has been granted.
//
// THE QUEUE CARRIES PRODUCT EVENTS AND NOTHING ELSE. The `criticalEvents` list
// in trackEvent() below (the lead_* trio and utm_visit) deliberately did NOT
// move onto it, and that was a considered reversal:
//   - they can fire BEFORE an authenticated session exists, and this endpoint
//     is authed-only, so batching would 401-drop them;
//   - keeping them on the existing singular endpoint means this branch carries
//     no same-deploy backend dependency for anything we cannot afford to lose.
// Product events are new, so a silent drop before the backend lands is fine.
// The conversion names (registration_success, the payment_* trio) are on
// NEITHER path any more - the server emits them; see BACKEND_ONLY_EVENTS.
//
// SURFACE ATTRIBUTION, current state (see the `surface` note in
// services/pushSubscriptionService.js):
//   - `contexts/AuthContext.js` passes `'logout'` on sign-out, and the service
//     SUPPRESSES `push_unsubscribed` for that surface entirely - a sign-out is
//     hygiene, not an opt-out, and counting it as one made the healthiest users
//     look like the ones abandoning push.
//   - `components/InstallPrompt.js` still calls `subscribe()` with no argument,
//     so its subscriptions report the honest `'unknown'` rather than a wrong
//     surface. That is the ONE genuinely-pending follow-up here: give it the
//     literal `'install_banner'` (that file is outside this workstream).

/**
 * Event names the SERVER writes about itself - never sent from here.
 *
 * Mirrors `BACKEND_ONLY_EVENT_NAMES` in `backend/app/schemas/analytics.py`
 * (minus `email_cta_clicked`, which no page has ever emitted). Both ingest
 * lanes answer 422 for these, so a page that still calls
 * `analytics.trackEvent('payment_success', ...)` would fire a request that can
 * only fail. `trackEvent` drops them instead of posting - that is why no page
 * had to change.
 *
 * WHY THE SERVER OWNS THEM (backend security pass 2f): an account exists
 * because the backend created and verified a row, and a charge succeeded
 * because HYP said so on a callback. A browser saying "payment_success" is an
 * assertion, not evidence - and these rows are kept forever and counted in
 * every admin funnel aggregate, so a minted one is a permanent fake
 * conversion. `auth.verify_email`, the Google new-account branch and the
 * payment callback now emit them directly.
 *
 * The GA4 surface is deliberately UNAFFECTED: gtag is a separate, third-party
 * funnel that has no server-side emitter, so these names still reach it exactly
 * as before. Only the first-party backend send is dropped.
 */
const BACKEND_ONLY_EVENTS = new Set([
  'registration_success',
  'payment_initiated',
  'payment_success',
  'payment_failure',
]);

/**
 * The homepage clients-row counter - one click on a customer's logo.
 *
 * Its own lane, on purpose. It is not a product event (the batch lane is
 * AUTHED, and almost nobody on the homepage has a session), so `trackEvent`
 * posts it to the singular /analytics/event lane like `utm_visit`.
 *
 * CONSENT-INDEPENDENT, on the `page_view` / `utm_visit` precedent (owner, 2026-09-06).
 * The row it writes carries no identifier of any kind: the backend forces
 * `user_id` NULL for this name on every request (it is SINGULAR_ONLY in
 * `schemas/analytics.py`), and the only parameter is the clicked client's slug,
 * off a server-side allowlist. It is a per-logo tally, not a visitor's
 * behaviour, so counting it for every visitor is what makes the number mean
 * "clicks on this logo" rather than "clicks by the consenting subset".
 *
 * The GA4 mirror above it stays consent-gated like every other gtag call - that
 * surface is a third party and is not ours to widen.
 *
 * Params are `{ client: <slug> }` and nothing else - the slug allowlist lives
 * server-side (`CLIENT_LOGO_SLUGS`) and mirrors `CLIENTS[].slug` in
 * `components/homepage/ClientsLive.js`; anything else is a 422, not a row.
 */
export const CLIENT_LOGO_CLICK_EVENT = 'client_logo_click';

// ---------------------------------------------------------------------------
// Conversion popup (public pages) - anonymous, sessionless funnel events
// ---------------------------------------------------------------------------
//
// impression -> open_form -> close | submit, counted for EVERY visitor whether
// or not they accepted analytics cookies (owner decision, under the cookie
// policy's anonymous-statistical-measurement exception). That exception holds
// only while nothing here identifies the visitor, so this lane is stricter than
// the client_logo_click one above:
//   - NO session id on the wire and NOTHING written to storage (sendToBackend
//     would mint `analytics_session_id` in sessionStorage - this does not use it);
//   - a bare fetch with credentials:'omit', like sendPageView, so no session
//     cookie or Authorization header rides along either;
//   - params are closed enums, exact key sets, nothing the visitor typed;
//   - no page path either (the backend forces page_path NULL for these names).
// The backend enforces the same (SINGULAR_ONLY + SESSIONLESS + a strict
// per-name validator in `schemas/analytics.py`). The GA4 mirror stays
// consent-gated like every other gtag call.

/** Where the popup is shown. Mirrors CONVERSION_POPUP_PAGE_TYPES in the backend. */
const CONVERSION_POPUP_PAGE_TYPES = Object.freeze(['home', 'profession', 'blog', 'landing']);

/**
 * The exact params each popup event carries: `{ name: { key: allowedValues } }`.
 *
 * HALF of a frontend/backend contract - the other half is
 * `CONVERSION_POPUP_PARAM_SPEC` in `backend/app/schemas/analytics.py`, and
 * `backend/tests/api/v1/test_conversion_popup_events.py` parses THIS literal to
 * hold the two equal. Keep the shape parseable: plain object entries, array
 * literals or CONVERSION_POPUP_PAGE_TYPES as values, no nested braces.
 */
export const CONVERSION_POPUP_PARAM_SPEC = Object.freeze({
  conversion_popup_impression: {
    page_type: CONVERSION_POPUP_PAGE_TYPES,
    trigger: ['time_scroll'],
  },
  conversion_popup_open_form: {
    page_type: CONVERSION_POPUP_PAGE_TYPES,
  },
  conversion_popup_close: {
    page_type: CONVERSION_POPUP_PAGE_TYPES,
    close_reason: ['button', 'escape'],
    step: ['1', '2'],
  },
  conversion_popup_submit: {
    page_type: CONVERSION_POPUP_PAGE_TYPES,
    method: ['email', 'google'],
  },
});
// Deep-freeze after the literal so the literal itself stays parseable (above).
Object.values(CONVERSION_POPUP_PARAM_SPEC).forEach((keys) => {
  Object.values(keys).forEach((values) => Object.freeze(values));
  Object.freeze(keys);
});

const isConversionPopupEvent = (eventName) =>
  typeof eventName === 'string' &&
  Object.prototype.hasOwnProperty.call(CONVERSION_POPUP_PARAM_SPEC, eventName);

/**
 * Reduce caller params to exactly what the backend accepts for `eventName`.
 *
 * Unknown keys are dropped. Every spec key is REQUIRED and must be a string
 * from its allowed list (no coercion - `step` is '1' / '2', never 1 / 2): the
 * backend 422s a missing or invalid key, so an event that cannot pass is not
 * sent at all rather than posted to fail.
 *
 * @param {string} eventName one of the four conversion_popup_* names
 * @param {object} params caller-supplied params (untrusted)
 * @returns {object|null} the clean params, or null when the event must not be sent
 */
export const buildConversionPopupParams = (eventName, params) => {
  if (!isConversionPopupEvent(eventName)) return null;
  const spec = CONVERSION_POPUP_PARAM_SPEC[eventName];
  const source = params && typeof params === 'object' ? params : {};
  const clean = {};
  const keys = Object.keys(spec);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = Object.prototype.hasOwnProperty.call(source, key) ? source[key] : undefined;
    if (typeof value !== 'string' || !spec[key].includes(value)) return null;
    clean[key] = value;
  }
  return clean;
};

/**
 * POST one popup event to the singular lane with no identifier of any kind.
 * Fire-and-forget: never awaited, never throws, failures are swallowed.
 *
 * @returns {boolean} true when the request was handed to fetch
 */
const sendSessionlessEvent = (eventName, eventParams) => {
  try {
    const base = (api && api.defaults && api.defaults.baseURL) || '';
    const pending = fetch(`${base}/analytics/event`, {
      method: 'POST',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      // No page_path: a path could re-link one visit's rows on a low-traffic
      // page, and the backend discards it for these names anyway. `page_type`
      // is the only "where" this event carries.
      body: JSON.stringify({
        event_name: eventName,
        event_params: eventParams,
        source: 'frontend',
      }),
      // A submit can be followed at once by a redirect (Google sign-in).
      keepalive: true,
    });
    if (pending && typeof pending.catch === 'function') pending.catch(() => {});
    return true;
  } catch (error) {
    // fetch unavailable - analytics must never break the page.
    return false;
  }
};

/**
 * Validate and emit one conversion-popup event. Never throws.
 *
 * @param {string} eventName one of the four conversion_popup_* names
 * @param {object} params see CONVERSION_POPUP_PARAM_SPEC
 * @param {boolean} consentGranted gates the GA4 mirror ONLY
 * @returns {boolean} true when the first-party event was sent
 */
const emitConversionPopupEvent = (eventName, params, consentGranted) => {
  try {
    const clean = buildConversionPopupParams(eventName, params);
    if (!clean) return false;

    if (consentGranted && typeof window !== 'undefined' && window.gtag) {
      try {
        window.gtag('event', eventName, {
          ...clean,
          ...tokenSafePageLocation(),
          send_to: MEASUREMENT_ID,
        });
      } catch (error) {
        /* GA4 failing must not cost the first-party count */
      }
    }

    return sendSessionlessEvent(eventName, clean);
  } catch (error) {
    return false;
  }
};

/** Product events that reach the first-party backend. Consent-GATED. */
export const PRODUCT_EVENTS = new Set([
  // App shell / PWA
  'app_route_viewed',
  'pwa_installed',
  'pwa_standalone_launch',
  // Push + notification preferences
  'push_permission_result',
  'push_subscribed',
  'push_unsubscribed',
  'email_pref_changed',
  'push_nudge_shown',
  'push_nudge_dismissed',
  'push_nudge_cta',
  // Trial phone ask (the standalone dashboard card and the first-steps
  // card's phone step). Properties: `stage` ('first'), plus `surface`
  // ('first_steps') from the first-steps step - enums only, never the
  // number, and no property name containing "phone".
  'phone_ask_shown',
  'phone_ask_dismissed',
  // Trial-conversion selling surfaces (navbar pill, dashboard offer banner,
  // checkout, card on file, the dashboard trial strip). Properties are ENUMS
  // AND NUMBERS ONLY - `surface` is one of 'navbar' | 'banner' | 'payment' |
  // 'card_on_file_banner' | 'card_on_file_payment' | 'strip', plus
  // `discount_percent` and `days_left` (the strip adds its `state`).
  // No price string, no copy, no lead text: these rows are kept for a year and
  // read in aggregate, and a free-text property is how PII gets into an
  // analytics table by accident.
  'upgrade_offer_shown',
  'upgrade_offer_dismissed',
  'upgrade_offer_cta',
  // Dashboard + lead funnel (already emitted to GA4; now also first-party)
  'dashboard_viewed',
  'lead_card_clicked',
  'lead_details_viewed',
  'lead_link_clicked',
  'lead_feedback_submitted',
  'message_composed',
  'message_copied',
  'lead_tab_changed',
  'lead_search_used',
  'lead_filter_applied',
  'lead_sort_changed',
  'pagination_used',
  // Email CTA landing. Fired once per session when the visit arrived through a
  // `?src=` tagged link in one of our own emails - a SEPARATE surface from the
  // opaque `?s=`/`?c=` marketing codes, which ride the consent-independent
  // utm_visit lane (see CaptureAcquisition). This one is a product event like
  // the rest of the list: consent-gated, batched, authed.
  'email_cta_landed',
  // New-user onboarding layer (PR E): the welcome dialog, the inline profession
  // ask on the dashboard, and the first-steps card. Properties are ENUMS AND
  // NUMBERS ONLY - `surface` is 'login' | 'reopen' for the dialog and
  // 'welcome' | 'empty_state' | 'profile' for a submit, `step` is a state-machine
  // name, `outcome` / `variant` are closed sets, `done_count` is 0..3. The
  // profession DESCRIPTION the user typed is never a property: it is free text a
  // person wrote about their own business, these rows are kept for a year, and a
  // free-text property is how PII reaches an analytics table by accident.
  //
  // This list is HALF of a cross-workstream contract - the other half is
  // `ALLOWED_EVENT_NAMES` / `BATCH_ONLY_EVENT_NAMES` in
  // `backend/app/schemas/analytics.py`, and a backend test parses this very
  // block to hold the two in step. A name added here and nowhere else is a 422
  // per batch, silently dropping the whole batch it rides in.
  'onboarding_welcome_shown',
  'onboarding_welcome_dismissed',
  'onboarding_profession_submitted',
  'onboarding_push_step_result',
  'onboarding_first_steps_shown',
  'onboarding_first_steps_cta',
  'onboarding_first_steps_dismissed',
  // "How did you hear about us" survey (partner program): the welcome dialog's
  // last optional step and its standalone fallback. Properties are `surface`
  // ('welcome' | 'standalone'), `answer` (an option KEY) and `has_detail`
  // (boolean) - NEVER the free text the user typed. The backend enforces the
  // same allowlist (`_restrict_source_survey_params`) and 422s anything else.
  'source_survey_shown',
  'source_survey_answered',
  'source_survey_skipped',
]);

const BATCH_PATH = '/analytics/events';
/** Server contract: at most 50 events per batch. */
const MAX_BATCH = 50;
/** Drop-oldest above this. A queue that grows without bound is a memory leak. */
const MAX_QUEUE = 200;
const FLUSH_INTERVAL_MS = 10000;

let eventQueue = [];
/** The single pending one-shot flush, or null when the queue is empty/drained. */
let flushTimer = null;
/** Set once the endpoint answers 404/422 - the branch has shipped ahead of the
 *  backend, or the payload shape drifted. Either way: stop, silently. */
let batchDisabled = false;
let dropLogged = false;
let listenersRegistered = false;

/** Cancel the pending one-shot flush, if any. */
const cancelScheduledFlush = () => {
  if (!flushTimer) return;
  try {
    clearTimeout(flushTimer);
  } catch (error) {
    /* nothing to clear */
  }
  flushTimer = null;
};

/** How many events this page load has thrown away, cumulative. */
let droppedTotal = 0;
/** The next `droppedTotal` at which a follow-up line is worth printing. */
let nextDropLogAt = 0;
/** Follow-up lines only every this many further drops - see noteDropped. */
const DROP_LOG_STEP = 100;

/**
 * Account for dropped events.
 *
 * The COUNT IS KEPT, not just the fact: `droppedTotal` is what tells someone
 * reading the console whether they lost three events during a token refresh or
 * two thousand behind a dead endpoint, and those are different problems.
 *
 * One line on the first drop, then a follow-up only every DROP_LOG_STEP further
 * drops. debug, never warn: a full queue or a rejected batch means the network
 * or the session is unhappy, which is not the user's problem and must not
 * colour the console.
 */
const noteDropped = (count) => {
  if (count <= 0) return;
  droppedTotal += count;

  if (!dropLogged) {
    dropLogged = true;
    nextDropLogAt = droppedTotal + DROP_LOG_STEP;
    console.debug(
      `[analytics] dropping queued events (queue full or batch rejected) - ${droppedTotal} dropped so far`,
    );
    return;
  }

  if (droppedTotal >= nextDropLogAt) {
    nextDropLogAt = droppedTotal + DROP_LOG_STEP;
    console.debug(`[analytics] ${droppedTotal} events dropped so far`);
  }
};

/**
 * Enforce MAX_QUEUE.
 *
 * `fromTail` exists for ONE caller: the 401 requeue. Trimming from the front is
 * correct everywhere else (drop-oldest), but a requeue unshifts the rescued
 * batch onto the front - so a front trim would delete exactly the events the
 * retry just saved, which is worse than never retrying. That path sheds the
 * newest instead.
 */
const trimQueue = ({ fromTail = false } = {}) => {
  let dropped = 0;
  while (eventQueue.length > MAX_QUEUE) {
    if (fromTail) {
      eventQueue.pop();
    } else {
      eventQueue.shift();
    }
    dropped += 1;
  }
  noteDropped(dropped);
};

/**
 * Throw away everything queued, without disabling the queue.
 *
 * Consent withdrawal only ever blocked NEW enqueues, which left up to 200
 * already-queued product events to ship on the next flush - after the user had
 * said no. Deliberately NOT counted as drops: this is the user's instruction
 * being honoured, not a failure, and it must not print anything.
 */
const discardQueuedEvents = () => {
  eventQueue = [];
  cancelScheduledFlush();
};

const batchUrl = () => {
  const base = (api && api.defaults && api.defaults.baseURL) || '';
  return `${base}${BATCH_PATH}`;
};

/**
 * The Authorization header for header-auth mode, or null in cookie mode (where
 * the cookie rides along on its own) and when no token is stored.
 */
const authorizationHeader = () => {
  if (USE_COOKIES) return null;
  try {
    const token = authService.getAccessToken();
    return token ? `Bearer ${token}` : null;
  } catch (error) {
    return null;
  }
};

const disableBatch = () => {
  batchDisabled = true;
  eventQueue = [];
  cancelScheduledFlush();
};

/**
 * Put a rejected batch back at the FRONT of the queue, once.
 *
 * WHY THIS EXISTS: these batches go out through a raw fetch, not the shared
 * axios instance, so they bypass api.js's 401 -> silent-refresh -> retry
 * interceptor entirely. The events are also spliced OUT of the queue before the
 * response arrives, so a 401 during a routine token refresh used to lose them
 * outright.
 *
 * BOUNDED AT ONE RETRY, and the bound is per EVENT, not per batch: `_retried`
 * is a non-enumerable marker (so JSON.stringify never serializes it into the
 * payload) set on the way back into the queue. A second 401 for the same event
 * drops it and counts the drop - an unbounded retry against an endpoint that
 * keeps answering 401 is a loop, not resilience.
 */
const requeueRejectedBatch = (events) => {
  if (batchDisabled) return;

  const retryable = events.filter((event) => !event._retried);
  noteDropped(events.length - retryable.length);
  if (retryable.length === 0) return;

  retryable.forEach((event) => {
    try {
      Object.defineProperty(event, '_retried', {
        value: true,
        enumerable: false,
        configurable: true,
      });
    } catch (error) {
      /* frozen object - it just gets one more chance than intended */
    }
  });

  eventQueue.unshift(...retryable);
  trimQueue({ fromTail: true });
  scheduleFlush();
};

/**
 * Ship one batch.
 *
 * TWO transports, and which one is used is not a style choice:
 *   - fetch(keepalive) is the TIMER path, because it is the only one whose
 *     status code we can read - that is how the 404/422 fallback ever learns
 *     the batch endpoint is not there yet.
 *   - sendBeacon is the UNLOAD path, because it is the one the browser
 *     guarantees to deliver after the document is gone. It cannot carry an
 *     Authorization header, so header-auth mode falls back to keepalive fetch
 *     (which is still allowed during pagehide).
 *
 * Never throws, never awaits, never logs on failure.
 */
const sendBatch = (events, { beacon }) => {
  const url = batchUrl();
  const body = JSON.stringify({ events });
  const header = authorizationHeader();

  if (beacon && !header) {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        // LOCAL DEV: this is expected to be silently dead and it is NOT a bug
        // to fix. The dev frontend (:3000) and API (:8000) are different
        // origins and `application/json` is not a CORS-safelisted Content-Type,
        // so the beacon needs a preflight it cannot complete on unload -
        // meanwhile sendBeacon returns TRUE (it only reports "queued"), so
        // there is nothing to detect and no fallback to take. In staging and
        // prod the API is same-origin behind the load balancer and no
        // preflight is involved. Verify unload flushes against a deployed
        // environment, never against localhost.
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch (error) {
      /* fall through to fetch below */
    }
  }

  try {
    fetch(url, {
      method: 'POST',
      // Cookie mode needs the session cookie on the wire; header mode carries
      // the token explicitly and must not widen credentials beyond same-origin.
      credentials: USE_COOKIES ? 'include' : 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(header ? { Authorization: header } : {}),
      },
      body,
      keepalive: true,
    })
      .then((response) => {
        if (!response) return;
        // The endpoint is not there (404) or refuses this shape (422): stand
        // down for the rest of the page's life rather than retrying forever.
        if (response.status === 404 || response.status === 422) {
          disableBatch();
          return;
        }
        // 401: almost always an access token that expired mid-session. The
        // shared axios instance would refresh and retry; a raw fetch cannot, so
        // the batch goes back in the queue for exactly one more attempt, by
        // which time some other request has usually refreshed the session.
        if (response.status === 401) {
          requeueRejectedBatch(events);
        }
      })
      .catch(() => {});
  } catch (error) {
    /* fetch unavailable - analytics must never break the app */
  }
};

/**
 * Drain the queue, MAX_BATCH events at a time.
 *
 * @param {object} [options]
 * @param {boolean} [options.beacon] prefer sendBeacon (page is going away)
 */
const flushQueue = ({ beacon = false } = {}) => {
  // The queue is about to be empty either way, so the pending one-shot has
  // nothing left to do. A 401 re-queue arms a fresh one.
  cancelScheduledFlush();
  if (batchDisabled || eventQueue.length === 0) return;
  while (eventQueue.length > 0) {
    sendBatch(eventQueue.splice(0, MAX_BATCH), { beacon });
  }
};

const handlePageHide = () => flushQueue({ beacon: true });

const handleVisibilityChange = () => {
  try {
    if (document.visibilityState === 'hidden') flushQueue({ beacon: true });
  } catch (error) {
    /* no document - nothing to flush against */
  }
};

/**
 * Arm the ONE-SHOT flush.
 *
 * A `setInterval` would tick every 10 seconds for the whole life of the tab,
 * finding an empty queue almost every time - a wakeup per 10s on a phone, for
 * nothing. A one-shot armed on the transition to non-empty and cleared by the
 * flush has identical latency (an event waits at most FLUSH_INTERVAL_MS) and
 * costs literally zero while the queue is empty.
 *
 * Idempotent: an already-armed timer is left alone, so the batch window is
 * measured from the FIRST event in it rather than being pushed back by each
 * new one.
 */
const scheduleFlush = () => {
  if (flushTimer || batchDisabled) return;
  try {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushQueue({ beacon: false });
    }, FLUSH_INTERVAL_MS);
    // Node/jest: an unref'd timer must not hold the process open.
    if (flushTimer && typeof flushTimer.unref === 'function') flushTimer.unref();
  } catch (error) {
    flushTimer = null;
  }
};

/**
 * Attach the unload flush listeners, once, on the first enqueue - so a page
 * that never emits a product event pays nothing.
 */
const ensureListeners = () => {
  if (listenersRegistered || batchDisabled) return;
  try {
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('pagehide', handlePageHide);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      listenersRegistered = true;
    }
  } catch (error) {
    /* no window - inert */
  }
};

/**
 * Queue one event for the batch endpoint. Consent is decided by the CALLER
 * (trackEvent), because the two event classes answer to different rules.
 *
 * @returns {boolean} true when the event actually entered the queue. Callers
 *   that persist a "already reported this once" flag MUST key it on this - see
 *   the standalone-launch one-shot in AnalyticsContext.
 */
const enqueueEvent = (eventName, parameters) => {
  if (batchDisabled) return false;

  let pagePath = '/';
  try {
    pagePath = normalizeRoutePath(window.location.pathname);
  } catch (error) {
    /* keep the '/' fallback */
  }

  eventQueue.push({
    event_name: eventName,
    event_params: parameters || {},
    session_id: safeSessionId(),
    page_path: pagePath,
    source: 'frontend',
  });
  trimQueue();

  ensureListeners();
  scheduleFlush();
  return true;
};

// ---------------------------------------------------------------------------
// Stored cookie-consent reader
// ---------------------------------------------------------------------------

/**
 * Read the analytics cookie-consent decision out of localStorage.
 *
 * ONE reader, exported, because there used to be two and they disagreed: the
 * cookie banner (components/CookieConsent/consentUtils.js) writes
 * `{ preferences: { analytics }, version, timestamp }`, this service read
 * `consent.preferences.analytics`, and AnalyticsContext read a top-level
 * `consent.analytics` that nothing ever wrote - so the context's `hasConsent`
 * was permanently false for every user who had accepted analytics cookies.
 *
 * Deliberately NOT version-aware: `getStoredConsent()` in consentUtils returns
 * null on a CONSENT_VERSION bump so the banner re-asks, but that is the
 * banner's job. This reader answers only "what did the user last say", which is
 * what this service has always acted on.
 *
 * @returns {boolean|null} true/false when a decision is stored, null when none
 *   is (or storage is unreadable).
 */
export const readStoredAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem('cookieConsent');
    if (!raw) return null;
    const consent = JSON.parse(raw);
    if (consent && consent.preferences && typeof consent.preferences.analytics === 'boolean') {
      return consent.preferences.analytics;
    }
    return null;
  } catch (error) {
    console.error('Error parsing cookie consent:', error);
    return null;
  }
};

class AnalyticsService {
  constructor() {
    this.initialized = false;
    this.consentGranted = false;
  }

  // Initialize analytics with consent check
  //
  // NOTHING HERE MAY DEPEND ON GA BEING LOADABLE except the gtag calls
  // themselves. `window.gtag` is absent for every visitor running an ad
  // blocker, and this function used to return on that check BEFORE reading
  // stored consent and BEFORE publishing `window.analytics` - so for those
  // users `consentGranted` stayed false forever, the cookie banner's accept
  // (which reaches this service only through `window.analytics`) went nowhere,
  // and the FIRST-PARTY product queue was silently dead. GA being blocked must
  // not block our own analytics.
  initialize() {
    const hasGtag = typeof window !== 'undefined' && !!window.gtag;

    if (hasGtag) {
      // Set initial consent state to denied. Must precede the stored-consent
      // replay below: a 'default' after an 'update' would overwrite it.
      window.gtag('consent', 'default', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }

    // Check localStorage for existing consent (shared reader - see above).
    // Runs with or without gtag: it sets the product-queue gate too.
    const storedConsent = readStoredAnalyticsConsent();
    if (typeof storedConsent === 'boolean') {
      this.updateConsent(storedConsent);
    }

    // Make analytics globally available for the consent system. Published even
    // without gtag - consentUtils.applyConsentPreferences() calls
    // `window.analytics.updateConsent(...)`, and that is the ONLY path by which
    // accepting the cookie banner reaches this service.
    if (typeof window !== 'undefined') {
      window.analytics = this;
    }

    if (!hasGtag) {
      console.warn('Google Analytics not available - may be blocked by ad blocker');
      return;
    }

    this.initialized = true;
  }

  // Update Google Analytics consent
  updateConsent(hasAnalyticsConsent) {
    // FIRST, and before any gtag guard: this flag gates the first-party product
    // queue as well as GA4. See the note on initialize().
    this.consentGranted = hasAnalyticsConsent;

    if (!hasAnalyticsConsent) {
      // Everything already queued goes in the bin, and the pending flush with
      // it. Blocking new enqueues alone would still have shipped up to
      // MAX_QUEUE events that were collected before the user said no.
      discardQueuedEvents();
    }

    // Same `typeof window` guard as initialize(): this runs from a module that
    // must stay importable outside a browser.
    if (typeof window === 'undefined' || !window.gtag) {
      // No GA to talk to. Cookie cleanup still matters - GA may have loaded on
      // an earlier page view of this session and left its cookies behind.
      if (!hasAnalyticsConsent) this.clearAnalyticsCookies();
      return;
    }

    // Update Google consent mode

    window.gtag('consent', 'update', {
      'analytics_storage': hasAnalyticsConsent ? 'granted' : 'denied'
    });

    if (hasAnalyticsConsent) {
      // Re-configure GA4 with measurement ID when consent is granted.
      //
      // send_page_view:false is REQUIRED here too, and this is the call that
      // is easiest to miss: it fires at the MOMENT a visitor accepts the
      // cookie banner. `send_page_view` is a per-config-call setting, not a
      // sticky property of the measurement id, so without it this re-enables
      // GA4's automatic page_view - which reports `page_location` = the FULL
      // url. On /supporter/confirm?token=... that ships a token that is
      // manufacturable consent. Three config calls exist (here, setUserId
      // below, and public/index.html) and ALL THREE need the flag.
      window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
    } else {
      // Clear GA cookies when consent is withdrawn
      this.clearAnalyticsCookies();
    }
  }

  // Clear Google Analytics cookies
  clearAnalyticsCookies() {
    const gaCookies = ['_ga', '_gid', '_gat', '_gat_gtag_' + MEASUREMENT_ID.replace('-', '_')];

    gaCookies.forEach(cookieName => {
      // Try to delete for different domain variations
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });

    // Also clear any GA cookies with dynamic IDs
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.startsWith('_ga_') || name.startsWith('_gat_')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
      }
    });
  }

  // Track custom events
  //
  // @returns {boolean} true only when the event entered the first-party product
  //   queue. False for a GA-only event, a transactional one, and - the case
  //   that matters - a product event held back by missing consent. A caller
  //   that writes a "reported once" flag must key it on this return value, or
  //   it will burn the flag on an event that never shipped.
  trackEvent(eventName, parameters = {}) {
    // The conversion popup's names have ONE route, whoever calls: the
    // sessionless lane below (validated, consent-independent first-party send,
    // consent-gated GA4). Checked FIRST so a stray trackEvent() call can neither
    // double the GA4 hit nor post a session id.
    if (isConversionPopupEvent(eventName)) {
      this.trackConversionPopupEvent(eventName, parameters);
      return false;
    }

    // Track to Google Analytics if available and consent granted
    if (window.gtag && this.consentGranted) {
      try {
        window.gtag('event', eventName, {
          ...parameters,
          // #349 R3-03, the belt: gtag reports `dl = document.location.href`
          // on every hit. While the address still holds a pay-link token
          // (the page moves it out at once), the hit is told the MASKED
          // location instead, so no event can carry the token to GA4.
          ...tokenSafePageLocation(),
          send_to: MEASUREMENT_ID
        });
      } catch (error) {
        console.error('Error tracking event to GA4:', error);
      }
    }

    // The server owns the conversion names now - see BACKEND_ONLY_EVENTS. GA4
    // above has already had this event; the first-party POST is dropped here,
    // silently and on purpose: the alternative is editing every page that still
    // calls trackEvent() with one of them, and a stray call would otherwise be
    // a guaranteed 422.
    if (BACKEND_ONLY_EVENTS.has(eventName)) {
      return false;
    }

    // The homepage clients-row counter: singular lane, anonymous server-side,
    // consent-INDEPENDENT (see CLIENT_LOGO_CLICK_EVENT). Never awaited - the
    // anchor opens in a new tab and this must not delay it.
    if (eventName === CLIENT_LOGO_CLICK_EVENT) {
      this.sendToBackend(eventName, parameters);
      return false;
    }

    // Also send critical events to backend
    const criticalEvents = [
      'lead_contact_initiated',
      'lead_assigned',
      'lead_status_updated',
      // Marketing attribution (#100). Reaches the FIRST-PARTY backend
      // consent-independently and is deliberately NOT tied to the analytics
      // cookie/GA4 surface — utm_visit is emitted via sendToBackend() directly
      // (see CaptureAcquisition), never through the GA path. Page views are NOT
      // listed here: they go ONLY through the dedicated sendPageView() beacon
      // below, and the backend has dropped 'page_view' from the /event
      // whitelist, so routing one through trackEvent() would 422.
      'utm_visit',
    ];

    // One immediate POST per event to the SINGULAR /analytics/event endpoint.
    // These are transactional records, not product analytics: they can fire
    // before an authenticated session exists (the batch endpoint is authed-only,
    // so a queued one would 401-drop) and they must not sit in a 10s window.
    // See the queue block above.
    if (criticalEvents.includes(eventName)) {
      this.sendToBackend(eventName, parameters);
      return false;
    }

    // Product events, and ONLY product events, ride the batch queue - gated on
    // the same analytics cookie-consent category as the GA4 call above.
    if (this.consentGranted && PRODUCT_EVENTS.has(eventName)) {
      return enqueueEvent(eventName, parameters);
    }

    return false;
  }

  /**
   * Emit one public-page conversion-popup event for EVERY visitor - see the
   * "Conversion popup" block near the top of this module. Never throws.
   *
   * @param {string} eventName one of the four conversion_popup_* names
   * @param {object} params exactly the keys in CONVERSION_POPUP_PARAM_SPEC
   * @returns {boolean} true when the first-party event was sent
   */
  trackConversionPopupEvent(eventName, params) {
    return emitConversionPopupEvent(eventName, params, this.consentGranted);
  }

  // ---------------------------------------------------------------------------
  // Marketing-attribution page-view beacon (#100).
  //
  // Fire-and-forget POST to the dedicated first-party /analytics/page-view
  // endpoint (no auth, 204). Consent-INDEPENDENT by design and carries NO user
  // identifier of any kind. Never blocks navigation; every failure is swallowed.
  // The GA4 page_view path (trackPageView) is a SEPARATE, consent-gated surface
  // and is left untouched.
  // ---------------------------------------------------------------------------
  sendPageView({ page_path, referrer_domain, s } = {}) {
    if (!page_path) return;
    try {
      const body = {
        // Masked here too (#349), not only by the caller: this is the sink.
        page_path: String(maskTokenPath(String(page_path))).slice(0, 255),
        // The in-memory anonymous-lane id, never analytics_session_id (see
        // the ANONYMOUS-LANE ID block at the top).
        session_id: getAnonymousLaneId(),
      };
      if (referrer_domain) body.referrer_domain = referrer_domain;
      if (s) body.s = s;

      // Deliberately a BARE fetch with credentials:'omit', NOT the shared axios
      // instance: in cookie-auth mode that instance sends withCredentials, which
      // would attach the httpOnly session cookie and put an identifier on the
      // wire — breaking the "no identifier at the network layer" promise for
      // this anonymous beacon. The URL is derived from the same base config the
      // axios instance uses so both point at the same API origin.
      const base = (api.defaults && api.defaults.baseURL) || '';
      // Fire and forget — never await, never surface an error to the UI.
      // keepalive lets the beacon complete even if the page is unloading.
      fetch(`${base}/analytics/page-view`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {
      // sessionStorage/fetch unavailable — attribution must never break the app.
    }
  }

  // Send analytics event to backend
  async sendToBackend(eventName, parameters = {}) {
    try {
      // Fire and forget - don't await the response
      api.post('/analytics/event', {
        event_name: eventName,
        event_params: parameters,
        // The anonymous singular names take the in-memory anonymous-lane id
        // (ANONYMOUS-LANE ID block); every other name keeps the session id.
        session_id: ANONYMOUS_SINGULAR_EVENTS.has(eventName)
          ? getAnonymousLaneId()
          : getSessionId(),
        // Masked (#349): this lane carries `utm_visit` from ANY landing page,
        // including an emailed pay link that was tagged with utm parameters.
        page_path: maskTokenPath(window.location.pathname),
        source: 'frontend'
      }).catch(error => {
        // Log error but don't block execution
        console.error('Failed to send analytics to backend:', error);
      });
    } catch (error) {
      // Silently fail - analytics shouldn't break the app
      console.error('Error preparing analytics event:', error);
    }
  }

  // Track page views
  //
  // `page_location` is sent EXPLICITLY, and it is the whole point of this call.
  // `page_path` is a Universal Analytics parameter that GA4 IGNORES: left to
  // itself GA4 derives `page_location` from `document.location` - the FULL url,
  // query string included. Two routes carry a secret there
  // (/supporter/confirm?token=... and /supporter/unsubscribe?token=...), and a
  // confirm token in Google's hands is manufacturable consent, which is exactly
  // what the rest of that flow is built to prevent.
  //
  // Turning off the AUTOMATIC page_view (send_page_view:false on all three
  // config calls) is not sufficient on its own, and this is the subtle half:
  // those pages strip `?token=` in an effect, but they are LAZY routes, so the
  // strip happens after the chunk loads - while the consent effect can fire a
  // page_view in that window (a visitor accepting the cookie banner re-fires
  // this for the current route). Passing an explicit origin+path means the
  // reported location can never include a query string no matter when it fires.
  //
  // `page_path` is kept alongside it: harmless, and any legacy report or
  // debug-view habit that reads it keeps working.
  trackPageView(path, title) {
    if (!window.gtag || !this.consentGranted) return;

    try {
      // Path only - never window.location.href, never location.search.
      const origin =
        (typeof window !== 'undefined' && window.location && window.location.origin) || '';
      // Masked at the sink as well as by the caller (#349).
      const safePath = maskTokenPath(path);
      window.gtag('event', 'page_view', {
        page_location: `${origin}${safePath}`,
        page_path: safePath,
        page_title: title,
        send_to: MEASUREMENT_ID
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  // Set user ID (hashed for privacy)
  setUserId(hashedId) {
    if (!window.gtag || !this.consentGranted || !hashedId) return;

    try {
      // send_page_view:false for the same reason index.html sets it on the
      // initial config: a REPEATED gtag('config', ...) for the same
      // measurement id fires another automatic page_view, and that one would
      // carry `page_location` with the full url - re-opening the token leak
      // (and the double count) at login time. The per-call default is true, so
      // the flag has to be repeated on every config call, not just the first.
      window.gtag('config', MEASUREMENT_ID, {
        user_id: hashedId,
        send_page_view: false
      });
    } catch (error) {
      console.error('Error setting user ID:', error);
    }
  }

  // Track user login
  trackLogin(method) {
    this.trackEvent('login', {
      method: method || 'email'
    });
  }

  // Track user signup
  trackSignup(method) {
    this.trackEvent('sign_up', {
      method: method || 'email'
    });
  }

  // Track lead view
  trackLeadView(leadId, category) {
    this.trackEvent('view_lead', {
      lead_id: leadId,
      category: category
    });
  }

  // Track lead action (contact, save, etc.)
  trackLeadAction(action, leadId, category) {
    this.trackEvent('lead_action', {
      action_type: action,
      lead_id: leadId,
      category: category
    });
  }

  // Track form submission
  trackFormSubmission(formName) {
    this.trackEvent('form_submit', {
      form_name: formName
    });
  }

  // Track search
  trackSearch(searchTerm, searchType) {
    this.trackEvent('search', {
      search_term: searchTerm,
      search_type: searchType
    });
  }

  // Track errors
  trackError(errorMessage, errorLocation) {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_location: errorLocation
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

// Export the instance and the class
export default analytics;
export { AnalyticsService, MEASUREMENT_ID };

/**
 * The conversion popup's emitter, bound to the singleton (so the GA4 mirror
 * follows the live cookie consent). Never throws.
 *
 * @param {string} eventName one of the four conversion_popup_* names
 * @param {object} params exactly the keys in CONVERSION_POPUP_PARAM_SPEC
 * @returns {boolean} true when the first-party event was sent
 */
export const trackConversionPopupEvent = (eventName, params) =>
  analytics.trackConversionPopupEvent(eventName, params);